import { API_BASE_URL, API_TOKEN } from '@/environment'
import { getAccessToken, getSubdomain } from '@/service/activeSession'
import axios from 'axios'
import { redirectToLogin } from './loginActions'
import { applyAxiosErrorInterceptor } from './utils'

const createConnection = () => {
  const conn = axios.create({
    baseURL: `${API_BASE_URL}/api/v1`,
    headers: {
      'API-Token': API_TOKEN,
    },
  })
  return applyAxiosErrorInterceptor(conn)
}

const authHeaders = async () => {
  return {
    Authorization: `Bearer ${await getAccessToken()}`,
    subdomain: await getSubdomain(),
  }
}

const handleResponse = (res) => {
  const result = {
    error: false,
    status: res.status,
    statusText: res.statusText,
    data: res.data,
    url: res.request?.responseURL,
    method: res.config?.method?.toUpperCase(),
  }
  return result
}

const handleError = (err) => {
  if (!err.response) {
    return {
      error: true,
      status: -1,
      statusText: 'Internet connection not Available',
      data: {},
      url: err.request?.responseURL,
      method: err.config?.method?.toUpperCase(),
    }
  }

  if (err.response.status === 401) {
    redirectToLogin()
  }

  return {
    error: true,
    status: err.response.status,
    statusText: err.response.statusText,
    data: err.response.data,
    url: err.request?.responseURL,
    method: err.config?.method?.toUpperCase(),
  }
}

export default {
  // setup axios instance
  connection: createConnection(),

  async getProducts() {
    try {
      const response = await this.connection.get('/products', {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getProductsSearch(params) {
    try {
      const response = await this.connection.get('/products/search', {
        headers: await authHeaders(),
        params,
      })

      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getProductById(id) {
    try {
      const response = await this.connection.get(`/products/${id}`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUsersSearch(options) {
    try {
      const response = await this.connection.get('/users/search', {
        params: options,
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getCurrentUser() {
    try {
      const response = await this.connection.get('/users/me', {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserGroups(userId) {
    try {
      const response = await this.connection.get(`/users/${userId}/groups`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getCurrentUserCompanies() {
    try {
      const response = await this.connection.get('/users/me/companies', {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async updateUserById(id, userData) {
    try {
      const response = await this.connection.patch(
        `/users/${id}`,
        { user: userData },
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async updateUsersMe(userData) {
    try {
      const response = await this.connection.patch(
        '/users/me',
        { user: userData },
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserById(id) {
    try {
      const response = await this.connection.get(`/users/${id}`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async createUser(userData) {
    try {
      const response = await this.connection.post(
        '/users',
        { user: userData },
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async createProduct(formDataPayload) {
    try {
      const response = await this.connection.post(
        '/products',
        formDataPayload,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async updateProductById(id, formDataPayload) {
    try {
      const response = await this.connection.patch(
        `/products/${id}`,
        formDataPayload,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async addProductToPolicies(productId, policyId, policyState) {
    try {
      const response = await this.connection.post(
        `policies/${policyId}/products`,
        {
          policy_product: {
            product_id: productId,
            state: policyState,
          },
        },
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getCurrentCompany() {
    try {
      const response = await this.connection.get('/companies/current', {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async toggleUserReportStatus(userId) {
    try {
      const response = await this.connection.post(
        `/users/${userId}/toggle/report`,
        {},
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserPortalStatus(userId) {
    try {
      const response = await this.connection.get(
        `/users/${userId}/products/status`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserProductStatus(userId, productId) {
    try {
      const response = await this.connection.get(
        `/users/${userId}/products/${productId}/status`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserProducts(userId, localeCode) {
    try {
      const response = await this.connection.get(
        `/users/${userId}/products?locale=${localeCode}`,
        {
          headers: await authHeaders(),
        }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserScenarios(userId) {
    try {
      const response = await this.connection.get(`/users/${userId}/scenarios`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserDepartments(userId) {
    try {
      const response = await this.connection.get(
        `/users/${userId}/departments?per_page=15000`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserScenarioCategories(userId, productId) {
    try {
      const response = await this.connection.get(
        `/users/${userId}/products/${productId}/scenario_categories`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserUncategorizedScenarios(userId, productId) {
    try {
      const response = await this.connection.get(
        `/users/${userId}/products/${productId}/uncategorized_scenarios`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserScenarioStatus(userId, scenarioId) {
    try {
      const response = await this.connection.get(
        `/users/${userId}/scenarios/${scenarioId}/status`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getUserStatusForScenarios(userId, scenarioIdList) {
    const promises = []

    scenarioIdList.forEach((scenarioId) => {
      promises.push(this.getUserScenarioStatus(userId, scenarioId))
    })

    return Promise.all(promises).then((results) => {
      const resultsData = []
      results.forEach((result) => {
        resultsData.push(result.data)
      })
      return resultsData
    })
  },

  async getUserEvents(userId) {
    try {
      const response = await this.connection.get(`/users/${userId}/courses`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getProductEvents(productId, options) {
    try {
      const response = await this.connection.get(
        `/products/${productId}/courses`,
        { params: options, headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async updateProductEventAttendees(productId, productEventId, attendeesArray) {
    try {
      const response = await this.connection.post(
        `/products/${productId}/courses/${productEventId}/users`,
        { user_ids: attendeesArray },
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  // Product Categories
  async getProductCategories(isJourney = false) {
    try {
      const response = await this.connection.get(
        `/product_categories?journey=${isJourney}`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async createProductCategory(categoryData) {
    try {
      const response = await this.connection.post(
        `/product_categories`,
        categoryData,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async updateProductCategoryById(id, categoryData) {
    try {
      const response = await this.connection.patch(
        `/product_categories/${id}`,
        categoryData,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async deleteProductCategoryById(id) {
    try {
      const response = await this.connection.delete(
        `/product_categories/${id}`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  /**
   * Reorder Product Categories' Positons
   * @param {Object} categoryIds - must have an array with category ids. e.g: product_category_ids: [4, 3, 2, 1, 5 ]
   */
  async updateProductCategoryPositons(categoryIds) {
    try {
      const response = await this.connection.patch(
        `/product_categories/reposition`,
        categoryIds,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  // Product categories: products
  async getProductCategoryProductsById(id) {
    try {
      const response = await this.connection.get(
        `/product_categories/${id}/products`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async removeProductsFromProductCategory(categoryId, productId) {
    try {
      const response = await this.connection.delete(
        `/product_categories/${categoryId}/products/${productId}`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },
  // End: Product Categories

  // Only caller is the legacy policy departments tab; goes with src/legacy.
  async getCurrentCompanyDepartments() {
    try {
      const response = await this.connection.get(
        `/companies/current/departments`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getCountryCodes() {
    try {
      const response = await this.connection.get(`/country_codes`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getCurrentCompanyPositions() {
    try {
      const response = await this.connection.get(
        `/companies/current/positions`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getCurrentCompanyDefaultGroups() {
    try {
      const response = await this.connection.get(
        `/companies/current/default_groups`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getGroupsOfType(groupType) {
    try {
      const response = await this.connection.get(
        `groups?group_type=${groupType}`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  // Groups
  async getGroupById(groupId) {
    try {
      const response = await this.connection.get(`/groups/${groupId}`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async getGroups() {
    try {
      const response = await this.connection.get(`/groups`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async createGroup(group) {
    try {
      const response = await this.connection.post(`/groups`, group, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async updateGroup(groupId, group) {
    try {
      const response = await this.connection.patch(
        `/groups/${groupId}`,
        group,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async deleteUserFromGroupById(groupId, userId) {
    try {
      const response = await this.connection.delete(
        `/groups/${groupId}/users/${userId}`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  // Policies
  async getAllPolicies() {
    try {
      const response = await this.connection.get(`/policies`, {
        headers: await authHeaders(),
      })
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async createPolicy(name, description, type) {
    try {
      const responseConnect = await this.connection.post(
        `policies`,
        {
          name,
          description,
          type,
        },
        { headers: await authHeaders() }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  async updatePolicy(policy) {
    try {
      const responseConnect = await this.connection.patch(
        `policies/${policy.id}`,
        {
          name: policy.name,
          description: policy.description,
        },
        { headers: await authHeaders() }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  // Policy Products
  async getProductPolicies(productId) {
    try {
      const responseConnect = await this.connection.get(
        `products/${productId}/policies`,
        { headers: await authHeaders() }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  async getProductsInPolicy(policyId) {
    try {
      const responseConnect = await this.connection.get(
        `/policies/${policyId}/products`,
        { headers: await authHeaders() }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  // Policy Groups
  async getPolicy(policyId) {
    try {
      const responseConnect = await this.connection.get(
        `/policies/${policyId}`,
        { headers: await authHeaders() }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  async getGroupsInPolicy(policyId, groupType) {
    const url = `/policies/${policyId}/groups${
      groupType ? `?group_type=${groupType}` : ''
    }`
    try {
      const responseConnect = await this.connection.get(url, {
        headers: await authHeaders(),
      })
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  async addGroupToPolicy(policyId, groups) {
    try {
      const responseConnect = await this.connection.post(
        `/policies/${policyId}/groups`,
        {
          group_ids: groups,
        },
        { headers: await authHeaders() }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  async removeGroupFromPolicy(policyId, groups) {
    try {
      const responseConnect = await this.connection.delete(
        `/policies/${policyId}/groups`,
        {
          data: {
            group_ids: groups,
          },
          headers: await authHeaders(),
        }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  // post scorm
  async createScorm(nameKey, file, progressCallback, signal) {
    const config = {
      headers: await authHeaders(),
      signal,
      onUploadProgress: (progressEvent) => {
        const totalLength = progressEvent.lengthComputable
          ? progressEvent.total
          : progressEvent.target.getResponseHeader('content-length') ||
            progressEvent.target.getResponseHeader(
              'x-decompressed-content-length'
            )
        if (totalLength !== null) {
          progressCallback(
            Math.round((progressEvent.loaded * 100) / totalLength)
          )
        }
      },
    }
    try {
      const response = await this.connection.post(
        `/chost/companies/${nameKey}/products?async=true`,
        file,
        config
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  async checkScormUploadStatus(nameKey, taskId) {
    try {
      const response = await this.connection.get(
        `/chost/companies/${nameKey}/products/status/${taskId}`,
        { headers: await authHeaders() }
      )
      return handleResponse(response)
    } catch (error) {
      return handleError(error)
    }
  },

  /* certify users */
  async postCertifyProduct(userId, productId, registrationId) {
    try {
      const responseConnect = await this.connection.post(
        `/users/${userId}/products/${productId}/certify?registration_id=${registrationId}`,
        {},
        { headers: await authHeaders() }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },

  async postCertifyScenario(userId, scenarioId) {
    try {
      const responseConnect = await this.connection.post(
        `/users/${userId}/scenarios/${scenarioId}/certify`,
        {},
        { headers: await authHeaders() }
      )
      return handleResponse(responseConnect)
    } catch (error) {
      return handleError(error)
    }
  },
}
